import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';

/**
 * @typedef {Object} UploadRequest
 * @property {string} file_path
 * @property {string} signed_url
 * @property {string} content_type
 */

/**
 * @typedef {Object} MultipartPart
 * @property {number} partNumber
 * @property {string} signedUrl
 */

/**
 * @typedef {Object} MultipartUploadRequest
 * @property {string} file_path
 * @property {Array<MultipartPart>} parts
 * @property {string} content_type
 */

/**
 * @typedef {Object} CompletedPart
 * @property {number} partNumber
 * @property {string} eTag
 */

/**
 * Upload a file to S3 using Tauri's native capabilities (single-part)
 * @param {string} filePath - The path to the file on the local system
 * @param {string} signedUrl - The pre-signed S3 URL
 * @param {string} contentType - The content type of the file
 * @param {Function} onProgress - Optional progress callback (percentage)
 * @returns {Promise<string>} - A promise that resolves with the upload result
 */
export async function uploadFileToS3(filePath, signedUrl, contentType, onProgress, options = {}) {
    const { signal, uploadId } = options;
    let removeAbortListener;
    try {
        if (signal?.aborted) {
            throw new Error('Upload cancelled');
        }
        const request = {
            file_path: filePath,
            signed_url: signedUrl,
            content_type: contentType
        };

        // Listen for progress events if callback provided
        let unlisten;
        if (onProgress) {
            unlisten = await listen('upload-progress', (event) => {
                const { percentage } = event.payload;
                onProgress(percentage);
            });
        }

        if (signal && uploadId) {
            const cancelNativeUpload = () => {
                invoke('cancel_s3_upload', { uploadId }).catch(() => {});
            };
            signal.addEventListener('abort', cancelNativeUpload, { once: true });
            removeAbortListener = () => signal.removeEventListener('abort', cancelNativeUpload);
        }

        const result = await invoke('upload_to_s3', { request: { ...request, upload_id: uploadId } });

        // Clean up listener
        if (unlisten) {
            unlisten();
        }

        console.log('Upload successful:', result);
        return result;
    } catch (error) {
        console.error('Upload failed:', error);
        throw error;
    } finally {
        removeAbortListener?.();
    }
}

/**
 * Upload a large file to S3 using multipart upload via Tauri
 * @param {string} filePath - The path to the file on the local system
 * @param {Array<{partNumber: number, signedUrl: string}>} parts - Array of signed URLs for each part
 * @param {string} contentType - The content type of the file
 * @param {Function} onProgress - Optional progress callback (percentage)
 * @returns {Promise<Array<CompletedPart>>} - Array of completed parts with ETags
 */
export async function uploadMultipartToS3(filePath, parts, contentType, onProgress, options = {}) {
    const { signal, uploadId } = options;
    let unlisten;
    let removeAbortListener;
    try {
        if (signal?.aborted) {
            throw new Error('Upload cancelled');
        }

        const request = {
            file_path: filePath,
            parts: parts.map(p => ({
                part_number: p.partNumber,
                signed_url: p.signedUrl
            })),
            content_type: contentType
        };

        console.log(`Starting multipart upload: ${parts.length} parts`);

        // Listen for progress events
        if (onProgress) {
            unlisten = await listen('upload-progress', (event) => {
                if (signal?.aborted) return;
                const { percentage, part_number, bytes_uploaded, total_bytes } = event.payload;
                console.log(`Part ${part_number}: ${percentage.toFixed(2)}% (${bytes_uploaded}/${total_bytes} bytes)`);
                onProgress(percentage);
            });
        }

        if (signal) {
            const cancelNativeUpload = () => {
                if (unlisten) {
                    try { unlisten(); } catch (_) {}
                }
                if (uploadId) {
                    invoke('cancel_s3_upload', { uploadId }).catch(() => {});
                }
            };
            signal.addEventListener('abort', cancelNativeUpload, { once: true });
            removeAbortListener = () => signal.removeEventListener('abort', cancelNativeUpload);
        }

        // Call Rust backend to upload all parts
        const completedParts = await invoke('upload_to_s3_multipart', { request: { ...request, upload_id: uploadId } });

        // Clean up listener
        if (unlisten) {
            unlisten();
        }

        if (signal?.aborted) {
            throw new Error('Upload cancelled');
        }

        console.log('Multipart upload successful:', completedParts);
        return completedParts;
    } catch (error) {
        if (unlisten) {
            try { unlisten(); } catch (_) {}
        }
        console.error('Multipart upload failed:', error);
        throw error;
    } finally {
        removeAbortListener?.();
    }
}
